import React, { memo, useCallback, useEffect, useState } from "react";
import { InputGroup, Form, Row, Col } from "react-bootstrap";
import moment from "moment";
import { fetchCreateAllowedZones, fetchProductPlan } from "../../../api";
import { useTimeline } from "../../../context/TimelineProvider";
import { formatDuration } from "../../Timeline/utils/time-format";
import { useDebounce } from "../../Timeline/hooks/useDebounce";
import { useTimelineMethods } from "../../Timeline/hooks/hooks";

function ReadyOperationCard({ operation, divisionId, orderItemId }) {
  const {
    addSegments,
    groups,
    segments,
    addSelectedoperationCard,
    selectedOperationCardId,
    updateLastClickTime,
    updateHash,
    resetFreeZoneSegments,
  } = useTimeline();

  const [inputValue, setInputValue] = useState("1");

  useEffect(() => {
    setInputValue("1");
  }, []);

  const hasSettings = operation.settings && operation.settings.length > 0;

  const initialGroupId = hasSettings ? operation.settings[0].group_id : null;
  const [selectedGroup, setSelectedGroup] = useState(initialGroupId);

  const changeSelectedGroup = (currentGroup) => {
    setSelectedGroup(currentGroup);
  };

  const selectedSetting = hasSettings
    ? operation.settings.find((setting) => setting.group_id === selectedGroup)
    : null;

  const initialBatchSize = selectedSetting ? selectedSetting.min : 1;
  const [batchSize, setBatchSize] = useState(initialBatchSize);
  const changeBatchSize = (curretBatchSize) => {
    setBatchSize(curretBatchSize);
  };

  const executionTime = selectedSetting ? selectedSetting.execution_time : 0;
  const parallel = batchSize || 1;
  const totalDurationSeconds =
    inputValue > 0 ? Math.ceil(inputValue / parallel) * executionTime : 0;

  // const { resetTimeline } = useTimelineMethods();

  // async function getProductData() {
  //   try {
  //     const response = await fetchProductPlan({ divisionId });
  //     const newHash = response?.data?.hash;

  //     if (newHash) {
  //       updateHash(response.data.hash);
  //     } else {
  //       console.log("No new hash received");
  //     }
  //   } catch (error) {
  //     console.error("Error fetching product plan:", error);
  //   }
  // }

  const formattedDuration = formatDuration(totalDurationSeconds);

  const handlePlacement = useCallback(async () => {
    if (!hasSettings || !selectedSetting) {
      console.error("Нет доступных настроек для размещения операции.");
      return;
    }
  
    if (selectedOperationCardId !== operation.operation_id) {
      resetFreeZoneSegments();
      addSelectedoperationCard(operation.operation_id);
    }
  
    const payload = {
      title: operation.operation_name,
      division_id: divisionId || "uuid",
      operation_quantity: inputValue,
      parallel_quantity: parallel,
      start_time: new Date().toISOString(),
      duration: totalDurationSeconds,
      order_item_id: orderItemId,
      operation_id: operation.operation_id,
      group_id: selectedGroup,
      equipment_id: selectedSetting.equipment_list[0],
    };
  
    if (inputValue > 0) {
      const res = await fetchCreateAllowedZones({
        order_item_id: payload.order_item_id,
        group_id: payload.group_id,
        operation_id: payload.operation_id,
        operation_quantity: inputValue,
        duration: totalDurationSeconds,
        time_now: moment().format("YYYY-MM-DD HH:mm:ss"),
      });
  
      if (!res?.data) {
        console.warn("⚠️ Нет данных от бэка. Пропускаем обработку.");
        return;
      }
  
      const allowedZonesData = res.data;
      const newFreeZones = [];
      const now = moment().valueOf();
  
      addSegments((prev) => prev.filter((s) => !s.id.startsWith("free-zone-")));
  
      for (const group of groups) {
        const groupAllowedZones = allowedZonesData.find(
          (zone) => zone.equipment_id === group.id
        );
  
        if (!groupAllowedZones) continue;
  
        const allowedZones = groupAllowedZones.allowed_zones?.map((zone) => ({
          start_time: Math.max(
            moment(zone.start_time, "YYYY-MM-DD HH:mm:ss").valueOf(),
            now
          ),
          end_time: moment(zone.end_time, "YYYY-MM-DD HH:mm:ss").valueOf(),
        })) || [];
  
        allowedZones.forEach((allowedZone, index) => {
          if (allowedZone.start_time < allowedZone.end_time) {
            newFreeZones.push({
              id: `free-zone-${group.id}-${index}`,
              group: group.id,
              title: "Free zone",
              start_time: allowedZone.start_time,
              end_time: allowedZone.end_time,
              operation_id: payload.operation_id,
              operation_start_time: allowedZone.start_time,
              operation_duration: totalDurationSeconds,
              operation_quantity: payload.operation_quantity,
              parallel_quantity: payload.parallel_quantity,
              order_item_id: payload.order_item_id,
              group_id: payload.group_id,
              equipment_id: group.id,
              canMove: false,
              canSelect: true,
              style: {
                background: "rgba(17, 204, 237, 0.95)",
                minHeight: "50px",
                cursor: "pointer",
              },
            });
          }
        });
      }
  
      if (newFreeZones.length > 0) {
        updateLastClickTime(Date.now());
        addSegments(newFreeZones);
      } else {
        console.warn("⚠️ newFreeZones пустой, не добавляем сегменты.");
      }
    }
  }, [
    inputValue,
    selectedGroup,
    batchSize,
    selectedSetting,
    selectedOperationCardId,
  ]);
  
  

  // const [debouncedHandlePlacement] = useDebounce(handlePlacement, 500);

  useEffect(() => {
    if (selectedOperationCardId === operation.operation_id && inputValue > 0) {
      handlePlacement();
    }
  }, [inputValue, selectedOperationCardId]);

  const handleChangeOperationQuantity = (e) => {
    const value = e.target.value;

    if (value === "") {
      setInputValue("");
      return;
    }

    const numericValue = Number(value);
    if (isNaN(numericValue)) {
      return;
    }

    const minValue = 1;
    const maxValue = operation.ready_quantity;

    const boundedValue = Math.max(minValue, Math.min(maxValue, numericValue));

    setInputValue(boundedValue.toString());
  };

  return (
    <div
      className="card mb-3"
      onClick={handlePlacement}
      style={{
        borderColor:
          operation.operation_id === selectedOperationCardId ? "blue" : "white",
        borderStyle: "solid",
        borderWidth: "6px",
      }}
    >
      <div className="card-header bg-success text-white">
        <div className="d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center">
            <h5 className="mb-0">
              {operation.operation_name} ({formattedDuration})
            </h5>
          </div>
        </div>
      </div>
      <div className="card-body text-success">
        <Row className="text-center mb-3">
          <Col>
            <p className="mb-0">
              <strong>Размещено:</strong>
            </p>
            <p>
              {operation.placed_quantity} из {operation.total_quantity}
            </p>
          </Col>
          <Col>
            <p className="mb-0">
              <strong>Доступно:</strong>
            </p>
            <p>{operation.ready_quantity}</p>
          </Col>
          <Col>
            <p className="mb-0">
              <strong>Разместить:</strong>
            </p>
            <InputGroup style={{ margin: "0 auto" }}>
              <Form.Control
                type="number"
                value={inputValue}
                onChange={handleChangeOperationQuantity}
              />
            </InputGroup>
          </Col>
        </Row>

        {hasSettings && selectedSetting ? (
          <>
            <Form.Group controlId="parallelProcessing" className="mt-3">
              <Form.Label>Группа оборудования</Form.Label>
              <Form.Select
                value={selectedGroup}
                onChange={(e) => {
                  changeSelectedGroup(e.target.value);
                  const newSetting = operation.settings.find(
                    (setting) => setting.group_id === e.target.value
                  );
                  changeBatchSize(newSetting ? newSetting.min : 1);
                }}
              >
                {operation.settings.map((setting) => (
                  <option key={setting.group_id} value={setting.group_id}>
                    {setting.group_name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            <Row className="mt-3 align-items-center">
              <Col xs={12}>
                <Form.Label>
                  Размер партии для параллельной обработки
                </Form.Label>
              </Col>
              <Col xs="auto">
                <Form.Label className="text-muted">
                  MIN: {selectedSetting.min}
                </Form.Label>
              </Col>
              <Col>
                <Form.Range
                  min={selectedSetting.min}
                  max={selectedSetting.max}
                  value={batchSize}
                  onChange={(e) => changeBatchSize(Number(e.target.value))}
                />
              </Col>
              <Col xs="auto">
                <Form.Label className="text-muted">
                  MAX: {selectedSetting.max}
                </Form.Label>
              </Col>
              <Col xs="auto" className="text-center">
                <Form.Control
                  type="number"
                  value={batchSize}
                  readOnly
                  className="text-center"
                  style={{ width: "80px", margin: "0 auto" }}
                />
              </Col>
            </Row>
          </>
        ) : (
          <div className="mt-3">
            <p className="text-danger">
              Нет доступных настроек для этой операции.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default memo(ReadyOperationCard);
